// @flow

import {useEffect, useRef, useState, useContext} from 'react';
import {pageDimensions} from '@pb/kit-util-page-dimensions';
import {DataTableContext} from '../contexts/data-table-context';
import {ScrollContext} from '../contexts/data-table-scroll-context';

import type {DataTableContextType} from '../contexts/data-table-context';
import type {ScrollContextType} from '../contexts/data-table-scroll-context';


type UseHeadersParams = {|
    getHeaderTopOffset?: () => number,
    hasPinnedRows: boolean,
    isMainHeaders: boolean,
    stickyReferenceElement?: HTMLElement | null,
    stickyScroll?: boolean,
    wrapperElement?: HTMLElement | null,
|};

type UseHeadersReturn = {|
    headerRef: {current: HTMLElement | null},
    headersContainer: {current: HTMLElement | null},
    isSticky: boolean,
    topPosition: number,
|};

export const useHeaders = (params: UseHeadersParams): UseHeadersReturn => {
    const {
        getHeaderTopOffset,
        hasPinnedRows,
        isMainHeaders,
        stickyReferenceElement,
    } = params;

    const headersContainer = useRef<HTMLElement | null>(null);
    const pinnedRowsContainerRef = useRef<HTMLElement | null>(null);
    const skipInitialCallbackRef = useRef<boolean>(true);

    const {mainHeaderHeightRef} = useContext<DataTableContextType>(DataTableContext);
    const {headerScrollContainerRef} = useContext<ScrollContextType>(ScrollContext);

    const [isSticky, setIsSticky] = useState<boolean>(false);
    const [topPosition, setTopPosition] = useState<number>(0);

    const pageHeaderHeight = pageDimensions.getHeaderHeight();

    const baseTopPosition = (() => {
        if (getHeaderTopOffset) {
            return getHeaderTopOffset();
        }

        if (stickyReferenceElement) {
            const computedStyle = getComputedStyle(stickyReferenceElement);
            const top = parseInt(computedStyle.top || 0);
            const height = stickyReferenceElement.offsetHeight;
            return top + height;
        }

        return pageHeaderHeight;
    })();

    useEffect(() => {
        const calculated = isMainHeaders
            ? baseTopPosition
            : baseTopPosition + (mainHeaderHeightRef?.current || 0);

        setTopPosition(calculated);
    }, [isMainHeaders, baseTopPosition]);

    useEffect(() => {
        const observer = new IntersectionObserver(([event]) => {
            if (skipInitialCallbackRef.current) {
                skipInitialCallbackRef.current = false;
                return;
            }

            if (!hasPinnedRows || (hasPinnedRows && !isMainHeaders)) {
                setIsSticky(event.intersectionRatio === 0 && event.boundingClientRect.top <= topPosition);
            }
        }, {
            threshold: [0],
            rootMargin: `${-1 * (topPosition || 0)}px`
        });

        if (headersContainer.current) {
            observer.observe(headersContainer.current);
        }

        return () => observer.disconnect();
    }, [headersContainer, topPosition, isMainHeaders, hasPinnedRows]);

    const headerRef = isMainHeaders ? headerScrollContainerRef : pinnedRowsContainerRef;

    return {
        headerRef,
        headersContainer,
        isSticky,
        topPosition
    };
};
